import { useEffect, useState } from 'react'
import { BackButton, DrawerSidebar, PageHeader, TableRowSkeleton, ToastSeverity, useToast } from '@/ui'
import {
  useProgressionProgramCreateMutate,
  useProgressionProgramQuery,
  useProgressionProgramUpdateMutate,
} from '@/layouts/progression-program/api'
import { generatePath, useNavigate, useParams } from 'react-router-dom'
import { ProgressionProgramSettingsForm } from '@/layouts/progression-program/widgets/ProgressionProgramSettingsForm'
import { URL_NEW } from '@/types'
import { ProgressionTiers } from '@/layouts/progression-program/widgets/ProgressionTiers'
import { FormProvider, useForm } from 'react-hook-form'
import { DisplayLevelType, ProgressionProgramRead, ProgressionProgramUpdate } from '@/api/dashboard'
import { getErrorText } from '@/api'
import { Button } from '@dashboard/ui'
import { PROGRESSIVE_PROGRAM_PATH, PROGRESSIVE_PROGRAM_PATH_FORM } from '@/libs'
import { useTranslation } from 'react-i18next'
import { ProgressionTierForm } from '@/layouts/progression-program/widgets/ProgressionTierForm'
import { ProgressionInfoBlock } from '@/layouts/progression-program/widgets/ProgressionInfoBlock'

export const ProgressionProgramEditFormPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { companyId, gameId, programId } = useParams() as { companyId: string; gameId: string; programId: string }
  const [modifyTierIndex, setModifyTierIndex] = useState<number | null>(null)
  const { data: program, isLoading } = useProgressionProgramQuery(companyId, gameId, programId, programId !== URL_NEW)
  const [settingsOpen, setSettingsOpen] = useState(programId == URL_NEW)
  const { mutateAsync: updateProgramAsync } = useProgressionProgramUpdateMutate(companyId, gameId)
  const { mutateAsync: createProgramAsync } = useProgressionProgramCreateMutate(companyId, gameId)
  const showToast = useToast()
  const methods = useForm<ProgressionProgramRead>({
    defaultValues: {
      settings: {
        rewards: [],
        display_level_type: DisplayLevelType.Current,
      },
      name: 'New Program',
      ...program,
    },
  })

  const {
    watch,
    handleSubmit,
    setValue,
    getValues,
    formState: { isDirty },
    reset,
  } = methods

  useEffect(() => {
    if (program?.id) {
      reset(program)
    }
  }, [program?.id])

  const onSubmit = async (data: ProgressionProgramRead) => {
    try {
      if (program?.id) {
        await updateProgramAsync({
          id: program.id,
          update: data as ProgressionProgramUpdate,
        })
      } else {
        const { data: createdProgram } = await createProgramAsync({
          create: data,
        })
        navigate(
          generatePath(PROGRESSIVE_PROGRAM_PATH_FORM, {
            companyId: companyId,
            gameId: gameId,
            programId: createdProgram.id,
          }),
        )
      }
      showToast({ message: t('saved'), severity: ToastSeverity.success })

      reset(data, { keepDirty: false })
    } catch (e) {
      showToast({ message: getErrorText(e), severity: ToastSeverity.error })
    }
  }

  return (
    <div className="flex size-full flex-col">
      <PageHeader
        sticky
        extra={
          <>
            <Button variant="outline" size="sm" color="primary" onClick={() => setSettingsOpen(true)}>
              {t('settings')}
            </Button>
            {isDirty && program?.id && (
              <Button variant="outline" color="secondary" size="sm" onClick={() => reset()}>
                {t('Discard')}
              </Button>
            )}
            <Button size="sm" disabled={!isDirty} onClick={handleSubmit(onSubmit)}>
              {t('Save')}
            </Button>
          </>
        }
      >
        <BackButton
          text={watch('name')}
          onClick={() => navigate(generatePath(PROGRESSIVE_PROGRAM_PATH, { companyId: companyId, gameId: gameId }))}
        />
      </PageHeader>

      <div className="relative flex grow gap-6">
        <div className="w-2/3">
          {isLoading ? (
            <TableRowSkeleton columnCount={1} rowCount={3} />
          ) : (
            <FormProvider {...methods}>
              <ProgressionTiers
                preview={false}
                setModifyTierIndex={idx => {
                  setModifyTierIndex(idx)
                }}
              />
            </FormProvider>
          )}
        </div>
        <div className="w-1/3">
          <ProgressionInfoBlock program={getValues() as ProgressionProgramRead} />
        </div>
      </div>

      <DrawerSidebar open={modifyTierIndex != null} width="720px" position="right">
        {modifyTierIndex != null && (
          <ProgressionTierForm
            tier={watch(`settings.rewards.${modifyTierIndex}`)}
            onClose={() => setModifyTierIndex(null)}
            onSubmit={data => {
              setValue(`settings.rewards.${modifyTierIndex}`, data, { shouldDirty: true })
            }}
          />
        )}
      </DrawerSidebar>

      <DrawerSidebar open={settingsOpen} width="780px" position="right">
        <FormProvider {...methods}>
          <ProgressionProgramSettingsForm
            onClose={() => setSettingsOpen(false)}
            program={getValues()}
            onSubmit={data => {
              setValue('name', data.name)
              setValue('description', data.description)
              setValue('title', data.title)
              setValue('content', data.content)
              setValue('start_at', data.start_at)
              setValue('end_at', data.end_at)
              setValue('settings', data.settings)
              setValue('main_item_id', data.main_item_id)
              setValue('pp_granting_item_ids', data.pp_granting_item_ids, { shouldDirty: true })
            }}
          />
        </FormProvider>
      </DrawerSidebar>
    </div>
  )
}
